<template>
  <div
    v-if="visible"
    class="fixed top-header left-main m-top-10 bg-normal overlay-modal"
    @click.self="close"
  >
    <div class="modal-content f-col fade-in-blur w-lay-1L bg-normal">
      <form
        @submit.prevent="
          step === 'email'
            ? handleEmailContinue()
            : step === 'register'
              ? handleRegister()
              : handleVerifyOtp()
        "
        class="f-col w-100"
      >
        <div class="relative block-i w-100">
          <label>{{ step === 'otp' ? 'OTP Code' : 'Email' }}</label>
          <input
            v-model="email"
            type="email"
            placeholder="Enter your email"
            required
            v-if="step === 'email' || step === 'register'"
          />
        </div>
        <input
          v-model="otpToken"
          type="text"
          placeholder="Enter the code from your email"
          required
          v-if="step === 'otp'"
        />

        <div class="f-row gap-1 w-100 m-top-2 m-left-0 just-center">
          <button
            type="submit"
            :disabled="loading"
            class="button-style btn-add2 radius-0 tr-all-03 just-c-c"
          >
            {{
              step === 'email'
                ? 'Continue'
                : step === 'register'
                  ? 'Send'
                  : 'Verify'
            }}
          </button>
        </div>
      </form>
    </div>
    <Transition name="fade">
      <div class="w-lay-1L" v-if="toastMessage">
        <div class="toast-message btn-add4 m-top-2 m-left-main w-fit z-1000">
          {{ toastMessage }}
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
// src/components/navigation/ModalLogin.vue
import { ref } from 'vue'
import usePocketbase from '@/composables/usePocketbase'
import { usePocketAuthStore } from '@/stores/usePocketAuthStore'
import { useToast } from '@/composables/useToast'

const props = defineProps({ visible: Boolean })
const emit = defineEmits(['close'])

const step = ref('email')
const email = ref('')
const name = ref('')
const otpToken = ref('')
const otpId = ref('')
const { toastMessage, showToast } = useToast()
const pb = usePocketbase()
const auth = usePocketAuthStore()
const loading = ref(false)

// === 1. Send OTP or switch to "register" ===
const handleEmailContinue = async () => {
  if (!email.value) {
    showToast('Enter your email')
    return
  }

  loading.value = true
  const normalizedEmail = email.value.trim().toLowerCase()

  try {
    // ✅ Try to request OTP directly — user must already exist
    const { otpId: id } = await pb
      .collection('users')
      .requestOTP(normalizedEmail)

    otpId.value = id
    step.value = 'otp'
    showToast('Code was sent, if email is registered.')
  } catch (err) {
    console.error('Code request failed:', err)
    showToast('No user found with that email.')
  } finally {
    loading.value = false
  }
}

// === 2. Register (create user, then OTP) ===
const handleRegister = async () => {
  if (!email.value || !name.value) {
    showToast('Please enter both name and email.')
    return
  }

  const normalizedEmail = email.value.trim().toLowerCase()
  const randomPassword = Math.random().toString(36).slice(-10)

  try {
    await pb.collection('users').create({
      email: normalizedEmail,
      password: randomPassword,
      passwordConfirm: randomPassword,
    })

    // immediately request an OTP
    const { otpId: id } = await pb
      .collection('users')
      .requestOTP(normalizedEmail)
    otpId.value = id
    step.value = 'otp'
    showToast('Account created. Login code sent.')
  } catch (err) {
    console.error('Registration failed:', err)
    showToast('Could not create user.')
  }
}

// === 3. Verify OTP ===
const handleVerifyOtp = async () => {
  if (!otpToken.value) {
    showToast('Enter the code you received')
    return
  }

  try {
    const authData = await pb
      .collection('users')
      .authWithOTP(otpId.value, otpToken.value)
    auth.user = authData.record

    try {
      auth.member = await pb
        .collection('members')
        .getFirstListItem(`user="${auth.user.id}"`)
    } catch (err) {
      if (err.status === 404) {
        auth.member = await pb.collection('members').create({
          user: auth.user.id,
          email: auth.user.email,
          name: '',
        })
      } else {
        throw err
      }
    }

    showToast('Logged in successfully.')
    reset()
    emit('close')
  } catch (err) {
    console.error(err)
    showToast('Invalid or expired code.')
  }
}

const reset = () => {
  email.value = ''
  name.value = ''
  otpToken.value = ''
  otpId.value = ''
  step.value = 'email'
}

const close = () => {
  reset()
  emit('close')
}
</script>
